import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Popconfirm, Row, Segmented, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { geoExposureApi } from './geoExposureApi';
import { formatDate, GeoExposureLayout, percent, RUN_STATUS_COLORS } from './GeoExposureLayout';
import { MentionChart } from './MentionChart';
import { QueryAnswersDrawer } from './QueryAnswersDrawer';

const { Text } = Typography;

const MENTION_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'mentioned', label: 'Mentioned' },
    { value: 'unmentioned', label: 'Not mentioned' },
];

const RUNNING_POLL_MS = 10 * 1000;

const runLabel = (run) => `${formatDate(run.createdAt)} · ${run.status}${run.completedCount ? ` · ${run.mentionedCount}/${run.completedCount} mentioned` : ''}`;

/** Answers: the latest run (or a picked one) per query, the mention share over time and the all-time totals. */
export const GeoExposureAnswers = () => {
    const [runsInfo, setRunsInfo] = useState(null);
    const [stats, setStats] = useState(null);
    const [runId, setRunId] = useState(null);
    const [run, setRun] = useState(null);
    const [mention, setMention] = useState('all');
    const [error, setError] = useState(null);
    const [loadingRun, setLoadingRun] = useState(false);
    const [starting, setStarting] = useState(false);
    const [open, setOpen] = useState(null);

    const loadOverview = useCallback(async () => {
        try {
            const [list, s] = await Promise.all([geoExposureApi.listRuns(), geoExposureApi.getStats()]);
            setRunsInfo(list);
            setStats(s);
            setRunId((current) => current ?? list.runs[0]?.id ?? null);
            setError(null);
        } catch (e) {
            setError(e.message);
        }
    }, []);

    const loadRun = useCallback(async () => {
        if (!runId) return;
        setLoadingRun(true);
        try {
            setRun(await geoExposureApi.getRun(runId, mention));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingRun(false);
        }
    }, [runId, mention]);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { loadRun(); }, [loadRun]);

    const running = runsInfo?.runs.some((r) => r.status === 'running');
    useEffect(() => {
        if (!running) return undefined;
        const timer = setInterval(() => { loadOverview(); loadRun(); }, RUNNING_POLL_MS);
        return () => clearInterval(timer);
    }, [running, loadOverview, loadRun]);

    const startRun = async () => {
        setStarting(true);
        try {
            const started = await geoExposureApi.startRun();
            message.success('Run started');
            setRunId(started.id);
            await loadOverview();
        } catch (e) {
            message.error(e.message);
        } finally {
            setStarting(false);
        }
    };

    const retry = async () => {
        try {
            await geoExposureApi.retryRun(runId);
            message.success('Retrying failed answers');
            await loadOverview();
            await loadRun();
        } catch (e) {
            message.error(e.message);
        }
    };

    const cancel = async () => {
        try {
            await geoExposureApi.cancelRun(runId);
            await loadOverview();
            await loadRun();
        } catch (e) {
            message.error(e.message);
        }
    };

    const totals = stats?.totals;
    const failed = run ? run.queries.reduce((n, q) => n + q.failed, 0) : 0;
    const pending = run ? run.queries.reduce((n, q) => n + q.answers - q.completed - q.failed, 0) : 0;
    const columns = [
        { title: 'Query', dataIndex: 'queryText', key: 'query', ellipsis: true },
        {
            title: 'Mentioned',
            key: 'mentioned',
            width: 140,
            sorter: (a, b) => a.mentioned / Math.max(1, a.completed) - b.mentioned / Math.max(1, b.completed),
            render: (_, r) => <Tag color={r.mentioned > 0 ? 'success' : 'default'}>{r.mentioned} / {r.completed}</Tag>,
        },
        { title: 'With citations', dataIndex: 'withCitations', key: 'citations', width: 130, align: 'right', render: (v, r) => `${v} / ${r.completed}` },
        { title: 'Links', dataIndex: 'links', key: 'links', width: 90, align: 'right' },
        {
            title: 'Failed',
            key: 'failed',
            width: 110,
            align: 'right',
            render: (_, r) => {
                const waiting = r.answers - r.completed - r.failed;
                if (r.failed) return <Text type="danger">{r.failed}</Text>;
                return waiting ? <Text type="secondary">{waiting} pending</Text> : '-';
            },
        },
    ];

    return (
        <GeoExposureLayout
            title="Answers"
            extra={(
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={() => { loadOverview(); loadRun(); }}>Refresh</Button>
                    <Popconfirm title="Ask ChatGPT every rephrase now?" description="Runs through Bright Data and costs per prompt." onConfirm={startRun} okText="Run now">
                        <Button type="primary" icon={<PlayCircleOutlined />} loading={starting} disabled={running}>Run now</Button>
                    </Popconfirm>
                </Space>
            )}
        >
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} md={6}><Card size="small" loading={!stats}><Statistic title="Mentioned (all runs)" value={totals?.mentioned ?? 0} suffix={`/ ${totals?.completed ?? 0}`} /></Card></Col>
                <Col xs={12} md={6}><Card size="small" loading={!stats}><Statistic title="Mention share (all runs)" value={percent(totals?.mentionedShare)} /></Card></Col>
                <Col xs={12} md={6}><Card size="small" loading={!stats}><Statistic title="Finished runs" value={totals?.runs ?? 0} /></Card></Col>
                <Col xs={12} md={6}>
                    <Card size="small" loading={!runsInfo}>
                        <Statistic title="Next run" value={runsInfo?.schedulerEnabled ? formatDate(runsInfo?.nextRunAt) : 'Scheduling off'} styles={{ content: { fontSize: 18 } }} />
                        {running && <Text type="secondary">A run is in progress</Text>}
                    </Card>
                </Col>
            </Row>

            <Card size="small" title="Share of answers that mentioned us, per run" style={{ marginBottom: 16 }}>
                <MentionChart series={stats?.series ?? []} onSelectRun={setRunId} />
            </Card>

            <Card
                size="small"
                title={(
                    <Space wrap>
                        <Text>Run</Text>
                        <Select
                            style={{ minWidth: 360 }}
                            value={runId}
                            onChange={setRunId}
                            placeholder="No runs yet"
                            options={(runsInfo?.runs ?? []).map((r) => ({ value: r.id, label: runLabel(r) }))}
                        />
                        {run && <Tag color={RUN_STATUS_COLORS[run.status]}>{run.status}</Tag>}
                        {run && <Text type="secondary">{run.mentionedCount}/{run.completedCount} mentioned · {percent(run.completedCount ? run.mentionedCount / run.completedCount : null)}{failed ? ` · ${failed} failed` : ''}{pending ? ` · ${pending} pending` : ''}</Text>}
                    </Space>
                )}
                extra={(
                    <Space>
                        {run?.status === 'running' && <Popconfirm title="Cancel this run?" onConfirm={cancel}><Button danger>Cancel</Button></Popconfirm>}
                        {run && run.status !== 'running' && failed > 0 && <Button onClick={retry}>Retry failed</Button>}
                        <Segmented options={MENTION_OPTIONS} value={mention} onChange={setMention} />
                    </Space>
                )}
            >
                {run?.error && <Alert type="error" showIcon message={run.error} style={{ marginBottom: 12 }} />}
                <Table
                    size="small"
                    rowKey={(r) => r.queryId || r.queryText}
                    loading={loadingRun}
                    dataSource={run?.queries ?? []}
                    columns={columns}
                    pagination={false}
                    onRow={(r) => ({ onClick: () => r.queryId && setOpen({ queryId: r.queryId, queryText: r.queryText }), style: { cursor: r.queryId ? 'pointer' : 'default' } })}
                    locale={{ emptyText: runId ? 'No queries match' : 'Start a run to collect answers' }}
                />
            </Card>

            <QueryAnswersDrawer queryId={open?.queryId} queryText={open?.queryText} runId={runId} onClose={() => setOpen(null)} />
        </GeoExposureLayout>
    );
};
